import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, inject, Input, input, OnInit, output, TemplateRef, viewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { take } from 'rxjs/operators';

import { OcJsonSchemaValidatorAjv } from '../../../../../core/oc-validator/oc-jsonSchemaValidatorAjv.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IError } from '@orgacard/login';

import { CoreService } from '../../../../../core/core.service';
import { EnvironmentService } from '../../../../../core/environment.service';
import { ocValidator } from '../../../../../core/oc-validator/oc.validator';
import { ModalService } from '../../../../../core/services/modal.service';
import { UtilityHelperService } from '../../../../../core/utility-helper.service';
import { DeletedType, LockedType, SystemMethod } from '../../../../shared/models/basic.model';
import { ArchiveType } from '../../../shared/models/archive.enum';
import { ISupplierContact, ISupplierContactDetail } from '../../../shared/models/supplier.model';
import { SupplierService } from '../../../shared/services/supplier.service';

@UntilDestroy()
@Component({
    selector: 'app-contacts',
    styleUrls: ['contacts.component.scss'],
    templateUrl: 'contacts.component.html',
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false,
})
export class ContactsComponent implements OnInit {
    private formBuilder = inject(UntypedFormBuilder);
    private modalService = inject(ModalService);
    environmentService = inject(EnvironmentService);
    coreService = inject(CoreService);
    supplierService = inject(SupplierService);
    ocJsonSchemaValidatorAjv = inject(OcJsonSchemaValidatorAjv);
    private readonly utilityHelperService = inject(UtilityHelperService);

    private readonly contactModal = viewChild<TemplateRef<any>>('contactModal');
    private readonly communicationModal = viewChild<TemplateRef<any>>('communicationModal');
    private readonly messageModal = viewChild<TemplateRef<any>>('messageModal');

    readonly updateable = input<boolean>(true);
    readonly chooseContact = input<boolean>(false);
    // !!! HINWEIS !!!
    // HINWEIS: privilegeContext funktioniert hier nicht !!! Weil supplierContactModel nur hier verwendet wird. Es gibt KEIN CONTACT-Modell (auch keine CONTACT-Tabelle) !!!!
    // @Input() privilegeContext: string = '';   // CASL Privileges: in welchem Context z.B. supplierBasedata etc. müssen die Privilegien geprüft werden
    @Input() public contacts: ISupplierContact[];
    public readonly supplierId = input<number>(undefined);
    readonly showDataContactChosen = input<boolean>(false);

    public readonly onDataContact = output<{
        data: ISupplierContact;
        method: SystemMethod;
    }>();
    public readonly onDataContactDetail = output<{
        data: ISupplierContactDetail;
        method: SystemMethod;
    }>();
    public readonly onDataContactChosen = output<{
        supplierContact: ISupplierContact;
        supplierContactDetail: ISupplierContactDetail;
    }>();

    // displayedColumns = ['favorite', 'emergency', 'name', 'department', 'edit', 'new'];
    public displayedColumnsContact = ['contactIcons', 'genderSdescr', 'lastName', 'firstName', 'department', 'buttons'];
    // expandedElement: PeriodicElement | null;
    public expandedElementSupplierContactDetail: ISupplierContactDetail | null;

    public contactModalHeader: string = '';
    public contactDetailModalHeader: string = '';
    public communicationNotSaved: boolean = true;

    public supplierContactForm: UntypedFormGroup;
    // convenience getter for easy access to form fields
    public get suppliercontactf() {
        return this.supplierContactForm.controls;
    }
    public supplierContactDetailForm: UntypedFormGroup;
    // convenience getter for easy access to form fields
    public get suppliercontactdetailf() {
        return this.supplierContactDetailForm.controls;
    }

    public modalMessageTitle: string = '';
    public modalMessageBody: string = '';

    public currentContactType: ArchiveType;
    public currentElement: ISupplierContact;
    public currentContactDetailElement: ISupplierContactDetail;

    ngOnInit() {
        //
        this.init();
        this.initSupplierContactFormValues();
        this.initSupplierContactDetailFormValues(undefined);
    }

    init() {
        this.coreService
            .getGenders(LockedType.NOTLOCKED, DeletedType.NOTDELETED)
            .pipe(take(1), untilDestroyed(this))
            .subscribe(
                (data: boolean) => {
                    //
                    this.completeContactData();
                    //
                },
                (err: IError) => {
                    this.utilityHelperService.handleComponentError(err);
                },
            );
    }

    showContact(supplierContact: ISupplierContact) {
        if (!supplierContact) {
            // NEW
            this.contactModalHeader = 'mws.contactmodalheadernew';
            this.initSupplierContactFormValues();
        } else {
            // EDIT
            this.contactModalHeader = 'mws.contactmodalheaderedit';
            this.fillSupplierContactFormValues(supplierContact);
        }

        this.modalService.openModal(this.contactModal(), { centered: false, backdrop: 'static' });
    }

    chooseContactDetail(element: ISupplierContact, contactDetailElement: ISupplierContactDetail) {
        //
        this.onDataContactChosen.emit({ supplierContact: element, supplierContactDetail: contactDetailElement });
    }

    deleteContact(element: ISupplierContact) {
        this.modalMessageTitle = 'mws.contactmodalheaderdelete';
        this.modalMessageBody = 'mws.contactmodalbodydelete';
        this.currentElement = element;
        this.currentContactType = ArchiveType.SUPPLIERCONTACT;

        this.modalService.openModal(this.messageModal(), { centered: false, backdrop: 'static' });
    }

    deleteContactDetail(element: ISupplierContact, contactDetailElement: ISupplierContactDetail) {
        this.modalMessageTitle = 'mws.contactdetailmodalheaderdelete';
        this.modalMessageBody = 'mws.contactdetailmodalbodydelete';
        this.currentElement = element;
        this.currentContactType = ArchiveType.SUPPLIERCONTACTDETAIL;
        this.currentContactDetailElement = contactDetailElement;

        this.modalService.openModal(this.messageModal(), { centered: false, backdrop: 'static' });
    }

    deleteElement() {
        switch (this.currentContactType) {
            case ArchiveType.SUPPLIERCONTACT:
                this.deleteContactElement();
                break;
            case ArchiveType.SUPPLIERCONTACTDETAIL:
                this.deleteContactDetailElement();
                break;
        }
        this.updateTable();
        this.undoTempValues();
    }

    deleteContactElement() {
        this.onDataContact.emit({ data: this.currentElement, method: SystemMethod.DELETE });
        // this.contacts = this.contacts.filter(elementRef => {
        //     return elementRef.id !== this.currentElement.id;
        // });
    }

    deleteContactDetailElement() {
        this.onDataContactDetail.emit({ data: this.currentContactDetailElement, method: SystemMethod.DELETE });
        // this.contacts.forEach(elementRef => {
        //     if (elementRef.id === this.currentElement.id) {
        //         this.currentElement.contactDetails = this.currentElement.contactDetails.filter(elementDetail => {
        //             return elementDetail.id !== this.currentContactDetailElement.id;
        //         });
        //         return;
        //     }
        // });
    }

    undoTempValues() {
        this.currentElement = null;
        this.currentContactDetailElement = null;
        this.currentContactType = null;
    }

    updateTable() {
        // neue Referenz erstellen, sonst wird die Table nicht aktualisiert !
        this.contacts = this.contacts.concat();
    }

    showCommunication(supplierContactId: number, supplierContactDetail: ISupplierContactDetail) {
        if (!supplierContactDetail) {
            // NEW
            // TODO: Mehrsprach
            this.contactDetailModalHeader = 'mws.contactdetailmodalheadernew';
            this.initSupplierContactDetailFormValues(supplierContactId);
        } else {
            // EDIT
            this.contactDetailModalHeader = 'mws.contactdetailmodalheaderedit';
            this.fillSupplierContactDetailFormValues(supplierContactDetail);
        }

        this.modalService.openModal(this.communicationModal(), { centered: false, backdrop: 'static' });
    }

    private fillContactDetailObj() {
        const supplierContactDetail: ISupplierContactDetail = {
            id: this.supplierContactDetailForm.get('id').value,
            supplierContactId: this.supplierContactDetailForm.get('supplierContactId').value,
            contactTypeId: this.supplierContactDetailForm.get('contactTypeId').value,
            contactInfo: this.supplierContactDetailForm.get('contactInfo').value,
        };

        return supplierContactDetail;
    }

    public saveContactDetail(closeModal: boolean) {
        // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
        // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
        const supplierContactDetail: ISupplierContactDetail = this.fillContactDetailObj();
        const result = this.ocJsonSchemaValidatorAjv.validateData('supplierContactDetail', supplierContactDetail);
        // if (this.supplierContactDetailForm.valid) {
        if (result.isValid) {
            const elementDetail: ISupplierContactDetail = {
                id: this.supplierContactDetailForm.get('id').value,
                supplierContactId: this.supplierContactDetailForm.get('supplierContactId').value,
                contactTypeId: this.supplierContactDetailForm.get('contactTypeId').value,
                contactInfo: this.supplierContactDetailForm.get('contactInfo').value,
            };

            this.onDataContactDetail.emit({ data: elementDetail, method: elementDetail.id ? SystemMethod.EDIT : SystemMethod.NEW });
            this.undoTempValues();
            if (closeModal) {
                this.modalService.closeAllModals();
            } else {
                if (elementDetail.id) {
                    // EDIT
                    // nothing to do
                } else {
                    // NEW: init form data
                    this.initSupplierContactDetailFormValues(elementDetail.supplierContactId);
                }
            }
        }
    }

    private fillContactObj(): ISupplierContact {
        const supplierContact: ISupplierContact = {
            id: this.supplierContactForm.get('id').value,
            firstName: this.supplierContactForm.get('firstName').value,
            lastName: this.supplierContactForm.get('lastName').value,
            department: this.supplierContactForm.get('department').value,
            genderId: this.supplierContactForm.get('genderId').value,
            contactDetails: [],
            supplierId: this.supplierService.supplierId,
            isEmergency: this.supplierContactForm.get('isEmergency').value,
            isFavorite: this.supplierContactForm.get('isFavorite').value,
        };
        return supplierContact;
    }

    public saveContact(closeModal: boolean) {
        // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
        // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
        const supplierContact: ISupplierContact = this.fillContactObj();
        const result = this.ocJsonSchemaValidatorAjv.validateData('supplierContact', supplierContact);
        // if (this.supplierContactForm.valid) {
        if (result.isValid) {
            this.onDataContact.emit({ data: supplierContact, method: supplierContact.id ? SystemMethod.EDIT : SystemMethod.NEW });

            if (closeModal) {
                this.modalService.closeAllModals();
            } else {
                if (supplierContact.id) {
                    // EDIT
                    // nothing to do
                } else {
                    // NEW: init form data
                    this.initSupplierContactFormValues();
                }
            }
        }
    }

    public completeContactData() {
        if (this.contacts && this.contacts.length > 0) {
            this.contacts.forEach((sc) => {
                if (this.environmentService.genderHash.has(sc.genderId)) {
                    sc['genderSdescr'] = this.environmentService.genderHash.get(sc.genderId).sdescr;
                }

                if (this.environmentService.genderHash.has(sc.genderId)) {
                    sc['genderDescr'] = this.environmentService.genderHash.get(sc.genderId).descr;
                }

                this.getAllSupplierContactsDetailsById(sc);
            });
        }
    }

    getAllSupplierContactsDetailsById(supplierContact: ISupplierContact) {
        if (supplierContact.contactDetails && supplierContact.contactDetails.length) {
            supplierContact.contactDetails.forEach((scd) => {
                if (this.environmentService.contactTypeHash.size > 0 && this.environmentService.contactTypeHash.get(scd.contactTypeId)) {
                    scd['contactTypeSdescr'] = this.environmentService.contactTypeHash.get(scd.contactTypeId).sdescr;
                    scd['contactTypeDescr'] = this.environmentService.contactTypeHash.get(scd.contactTypeId).descr;
                    scd['contactTypeStyleClass'] = this.environmentService.contactTypeHash.get(scd.contactTypeId).styleClass;
                } else {
                    scd['contactTypeSdescr'] = 'UNKNOWN';
                    scd['contactTypeDescr'] = 'UNKNOWN';
                    scd['contactTypeStyleClass'] = 'UNKNOWN';
                }
            });
        }
    }

    initSupplierContactFormValues() {
        //            shortdescription: ['', [Validators.required, Validators.minLength(2)]],
        this.supplierContactForm = this.formBuilder.group({
            id: undefined,
            supplierId: this.supplierId(),
            genderId: {
                value: null,
                disabled: !this.updateable() || !this.coreService.checkAbility('create', 'suppliercontact.genderid'),
            },
            department: [
                {
                    value: '',
                    disabled: !this.updateable() || !this.coreService.checkAbility('create', 'suppliercontact.department'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplierContact',
                        'department',
                        this.coreService.getMsgkey('mws.department', undefined),
                    ),
                ],
            ],
            lastName: [
                {
                    value: '',
                    disabled: !this.updateable() || !this.coreService.checkAbility('create', 'suppliercontact.lastname'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplierContact',
                        'lastName',
                        this.coreService.getMsgkey('mws.surname', undefined),
                    ),
                ],
            ],
            firstName: [
                {
                    value: '',
                    disabled: !this.updateable() || !this.coreService.checkAbility('create', 'suppliercontact.firstname'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplierContact',
                        'firstName',
                        this.coreService.getMsgkey('mws.firstname', undefined),
                    ),
                ],
            ],
            isFavorite: {
                value: false,
                disabled: !this.updateable() || !this.coreService.checkAbility('create', 'suppliercontact.favorite'),
            },
            isEmergency: {
                value: false,
                disabled: !this.updateable() || !this.coreService.checkAbility('create', 'suppliercontact.emergency'),
            },
            changed: false,
        });
        this.supplierContactForm.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            // console.log('name has changed:', value);

            // !!! FUNKTIONIER SO NICHT !!!
            // if (!this.supplierContactForm.get('changed').touched && this.supplierContactForm.valid) {
            //     this.supplierContactForm.get('changed').markAsTouched();
            // } else if (!this.supplierContactForm.valid) {
            //     this.supplierContactForm.get('changed').markAsUntouched();
            // }
            // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
            // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
            const result = this.ocJsonSchemaValidatorAjv.validateData('supplierContact', this.supplierContactForm.value);
            if (!this.supplierContactForm.get('changed').touched && result.isValid) {
                this.supplierContactForm.get('changed').markAsTouched();
            } else if (!result.isValid) {
                this.supplierContactForm.get('changed').markAsUntouched();
            }
        });
    }

    fillSupplierContactFormValues(supplierContact: ISupplierContact) {
        this.supplierContactForm = this.formBuilder.group({
            id: supplierContact.id,
            genderId: {
                value: supplierContact.genderId,
                disabled: !this.updateable() || !this.coreService.checkAbility('update', 'suppliercontact.genderid'),
            },
            department: [
                {
                    value: supplierContact.department,
                    disabled: !this.updateable() || !this.coreService.checkAbility('update', 'suppliercontact.department'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplierContact',
                        'department',
                        this.coreService.getMsgkey('mws.department', undefined),
                    ),
                ],
            ],
            lastName: [
                {
                    value: supplierContact.lastName,
                    disabled: !this.updateable() || !this.coreService.checkAbility('update', 'suppliercontact.lastname'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplierContact',
                        'lastName',
                        this.coreService.getMsgkey('mws.surname', undefined),
                    ),
                ],
            ],
            firstName: [
                {
                    value: supplierContact.firstName,
                    disabled: !this.updateable() || !this.coreService.checkAbility('update', 'suppliercontact.firstname'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplierContact',
                        'firstName',
                        this.coreService.getMsgkey('mws.firstname', undefined),
                    ),
                ],
            ],
            isFavorite: {
                value: supplierContact.isFavorite,
                disabled: !this.updateable() || !this.coreService.checkAbility('update', 'suppliercontact.favorite'),
            },
            isEmergency: {
                value: supplierContact.isEmergency,
                disabled: !this.updateable() || !this.coreService.checkAbility('update', 'suppliercontact.emergency'),
            },
            changed: false,
        });
        this.supplierContactForm.valueChanges.pipe(untilDestroyed(this)).subscribe((change) => {
            // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
            // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
            const sContact: ISupplierContact = this.fillContactObj();
            const result = this.ocJsonSchemaValidatorAjv.validateData('supplierContact', sContact);

            if (!this.supplierContactForm.get('changed').touched && result.isValid) {
                this.supplierContactForm.get('changed').markAsTouched();
            } else if (!result.isValid) {
                this.supplierContactForm.get('changed').markAsUntouched();
            }
        });
    }

    initSupplierContactDetailFormValues(supplierContactId: number) {
        this.supplierContactDetailForm = this.formBuilder.group({
            id: undefined,
            supplierContactId,
            contactInfo: [
                {
                    value: undefined,
                    disabled: !this.coreService.checkAbility('create', 'supplier.contact.detail.contactinfo'),
                },
                [
                    ocValidator(
                        this.ocJsonSchemaValidatorAjv,
                        this.environmentService,
                        'supplierContactDetail',
                        'contactInfo',
                        this.coreService.getMsgkey('mws.entry', undefined),
                    ),
                ],
            ],
            contactTypeId: {
                value: this.environmentService.contactTypeList[0].id,
                disabled: !this.coreService.checkAbility('create', 'supplier.contact.detail.contacttypeid'),
            },
            changed: false,
        });
        this.supplierContactDetailForm.valueChanges.pipe(untilDestroyed(this)).subscribe((change) => {
            // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
            // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
            const supplierContactDetail: ISupplierContactDetail = this.fillContactDetailObj();
            const result = this.ocJsonSchemaValidatorAjv.validateData('supplierContactDetail', supplierContactDetail);
            // if (!this.supplierContactDetailForm.get('changed').touched && this.supplierContactForm.valid) {
            if (!this.supplierContactDetailForm.get('changed').touched && result.isValid) {
                this.supplierContactDetailForm.get('changed').markAsTouched();
            } else if (!result.isValid) {
                this.supplierContactDetailForm.get('changed').markAsUntouched();
            }
        });
    }

    fillSupplierContactDetailFormValues(supplierContactDetail: ISupplierContactDetail) {
        this.supplierContactDetailForm = this.formBuilder.group({
            id: supplierContactDetail.id,
            supplierContactId: supplierContactDetail.supplierContactId,
            contactInfo: [
                {
                    value: supplierContactDetail.contactInfo,
                    disabled: !this.coreService.checkAbility('update', 'supplier.contact.detail.contactinfo'),
                },
                [ocValidator(this.ocJsonSchemaValidatorAjv, this.environmentService, 'ContractDetail', 'contactInfo')],
            ],
            contactTypeId: {
                value: supplierContactDetail.contactTypeId,
                disabled: this.coreService.checkAbility('update', 'supplier.contact.detail.contacttypeid'),
            },
            changed: false,
        });
        this.supplierContactDetailForm.valueChanges.pipe(untilDestroyed(this)).subscribe((change) => {
            // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
            // PRÜFEN OB FORM VALID IST !!! NICHT VIA this.xxxxxxForm.valid
            const sContactDetail: ISupplierContactDetail = this.fillContactDetailObj();
            const result = this.ocJsonSchemaValidatorAjv.validateData('supplierContactDetail', sContactDetail);
            // if (!this.supplierContactDetailForm.get('changed').touched && this.supplierContactForm.valid) {
            if (!this.supplierContactDetailForm.get('changed').touched && result.isValid) {
                this.supplierContactDetailForm.get('changed').markAsTouched();
            } else if (!result.isValid) {
                this.supplierContactDetailForm.get('changed').markAsUntouched();
            }
        });
    }
}
